import { D2KInstance } from './types';

// The most D2K instances one request creates: the server's limit.
export const MAX_D2K_INSTANCES = 10;

/**
 * derivedName names the instance at index after the first instance's name,
 * as test, test-02, test-03...
 */
export function derivedName(firstName: string, index: number) {
  if (!firstName) {
    return '';
  }

  return `${firstName}-${String(index + 1).padStart(2, '0')}`;
}

/**
 * resize sets how many instances there are: new ones are named after the
 * first, and the last ones go first.
 */
export function resize(
  instances: D2KInstance[],
  quantity: number
): D2KInstance[] {
  const count = Math.min(Math.max(quantity, 1), MAX_D2K_INSTANCES);
  const firstName = instances[0]?.name ?? '';

  return Array.from(
    { length: count },
    (_, index) =>
      instances[index] ??
      (index === 0
        ? { name: '', auto: false }
        : { name: derivedName(firstName, index), auto: true })
  );
}

/**
 * rename names the instance at index. Renaming the first renames the
 * instances whose names are still derived from it; renaming another one
 * makes its name its own.
 */
export function rename(
  instances: D2KInstance[],
  index: number,
  name: string
): D2KInstance[] {
  return instances.map((instance, i) => {
    if (i === index) {
      return { name, auto: false };
    }

    if (index === 0 && instance.auto) {
      return { name: derivedName(name, i), auto: true };
    }

    return instance;
  });
}

/** remove removes the instance at index. The first one always stays. */
export function remove(instances: D2KInstance[], index: number): D2KInstance[] {
  if (index === 0) {
    return instances;
  }

  return instances.filter((_, i) => i !== index);
}
